import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Scale, TrendingUp, Loader2, AlertTriangle, ArrowRight, Upload, Receipt, Check, Trash2, FileText } from 'lucide-react';

const money = (v) => {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  const s = `$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return n < 0 ? `-${s}` : s;
};

// A cell that NEVER shows a fabricated $0 for a missing value.
const Amount = ({ value, missingLabel = 'Not yet recorded', testid, strong, tone }) => (
  <div className={`text-sm ${strong ? 'font-bold' : 'font-semibold'} ${tone || 'text-[#0E2A47]'}`} data-testid={testid}>
    {value === null || value === undefined ? <span className="text-xs font-medium italic text-slate-400">{missingLabel}</span> : money(value)}
  </div>
);

const Row = ({ label, children, hint }) => (
  <div className="flex items-start justify-between gap-3 py-1.5">
    <div className="min-w-0">
      <div className="text-xs font-semibold text-slate-600">{label}</div>
      {hint && <div className="text-[11px] text-slate-400">{hint}</div>}
    </div>
    <div className="text-right">{children}</div>
  </div>
);

export default function FinancialReconciliation({ workOrderId, token, canEdit, refreshKey, onApplied }) {
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [sum, setSum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [draft, setDraft] = useState(null);
  const [savingR, setSavingR] = useState(false);

  const loadReceipts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/work-orders/${workOrderId}/receipts`, authHeaders);
      setReceipts(r.data?.receipts || []);
    } catch { /* ops-only */ }
  }, [workOrderId, token]);

  const load = useCallback(async () => {
    setSum((prev) => { if (!prev) setLoading(true); return prev; });
    try {
      const r = await axios.get(`${API}/work-orders/${workOrderId}/financial-summary`, authHeaders);
      setSum(r.data);
    } catch { /* non-ops roles never see this section */ }
    finally { setLoading(false); }
  }, [workOrderId, token, refreshKey]);
  useEffect(() => { load(); loadReceipts(); }, [load, loadReceipts]);

  const onExtract = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/work-orders/${workOrderId}/receipts/extract`, fd,
        { headers: { ...authHeaders.headers, 'Content-Type': 'multipart/form-data' } });
      const d = r.data || {};
      if (d.duplicate) toast.warning('This receipt looks like a duplicate — review before saving.');
      setDraft({
        supplier: d.supplier || '', receipt_date: d.receipt_date || '', receipt_reference: d.receipt_reference || '',
        subtotal: d.subtotal ?? '', tax: d.tax ?? '', total: d.total ?? '',
        content_hash: d.content_hash || '', media_id: d.media_id || null, duplicate: !!d.duplicate,
        note: d.ok === false ? (d.error || 'Could not read automatically — enter the amounts.') : '',
      });
      if (d.ok === false) toast.info('Enter the receipt amounts manually.');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not read the receipt'); }
    finally { setExtracting(false); }
  };

  const saveReceipt = async () => {
    if (!draft) return;
    if (!(Number(draft.total) > 0)) return toast.error('Enter the receipt total');
    setSavingR(true);
    try {
      await axios.post(`${API}/work-orders/${workOrderId}/receipts`, {
        supplier: draft.supplier || null, receipt_date: draft.receipt_date || null,
        receipt_reference: draft.receipt_reference || null,
        subtotal: draft.subtotal === '' ? null : Number(draft.subtotal),
        tax: draft.tax === '' ? null : Number(draft.tax), total: Number(draft.total),
        content_hash: draft.content_hash || null, media_id: draft.media_id || null, verified: true,
        allow_duplicate: draft.duplicate ? false : undefined,
      }, authHeaders);
      toast.success('Receipt recorded as a verified actual cost');
      setDraft(null);
      await loadReceipts(); await load(); onApplied?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not save the receipt'); }
    finally { setSavingR(false); }
  };

  const toggleVerify = async (r) => {
    try {
      await axios.patch(`${API}/work-orders/${workOrderId}/receipts/${r.id}`, { verified: !r.verified }, authHeaders);
      await loadReceipts(); await load();
    } catch { toast.error('Could not update the receipt'); }
  };
  const removeReceipt = async (r) => {
    try {
      await axios.delete(`${API}/work-orders/${workOrderId}/receipts/${r.id}`, authHeaders);
      toast.success('Receipt removed'); await loadReceipts(); await load();
    } catch { toast.error('Could not remove the receipt'); }
  };
  const openReceipt = async (r) => {
    if (!r.media_id) return;
    try {
      const resp = await axios.get(`${API}/work-orders/${workOrderId}/media/${r.media_id}`,
        { ...authHeaders, responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Could not open the original receipt'); }
  };
  const money2 = (v) => (v === null || v === undefined || v === '' ? '—' : money(Number(v)));

  const applyApprovedPrice = async () => {
    setApplying(true);
    try {
      await axios.post(`${API}/work-orders/${workOrderId}/apply-approved-price`, {}, authHeaders);
      toast.success('Approved TAG-Work price carried into client billing');
      await load();
      onApplied?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not apply the approved price'); }
    finally { setApplying(false); }
  };

  if (loading) return (
    <Card className="border-slate-200 shadow-sm rounded-2xl overflow-hidden" data-testid="wo-section-financial">
      <CardHeader className="bg-[#13315C] rounded-t-2xl px-4 py-2.5"><CardTitle className="text-sm sm:text-base font-bold uppercase tracking-wide text-white flex items-center gap-2"><Scale size={15} /> 4 · Financial Reconciliation</CardTitle></CardHeader>
      <CardContent className="py-6 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" size={18} /></CardContent>
    </Card>
  );
  if (!sum) return null;

  const tw = sum.tagwork;
  const cb = sum.client_billing;
  const ac = sum.actual_costs;
  const v = sum.variance;
  const f = sum.flags;

  return (
    <Card className="border-slate-200 shadow-sm rounded-2xl overflow-hidden" data-testid="wo-section-financial">
      <CardHeader className="bg-[#13315C] rounded-t-2xl px-4 py-2.5">
        <CardTitle className="text-sm sm:text-base font-bold uppercase tracking-wide text-white flex items-center gap-2"><Scale size={15} /> 4 · Financial Reconciliation</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 pt-4">
        {/* Missing client charge while an approved selling price exists — the exact disconnect to fix. */}
        {f.client_charge_missing && f.selling_price_present && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="wo-fin-price-mismatch">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-amber-800">Client billing is blank, but this job has an approved price</div>
                <div className="text-xs text-amber-700">The governing TAG-Work budget approved <b>{money(sum.approved_selling_price)}</b>. Client billing currently shows no charge — this is <b>missing</b>, not $0.</div>
                {canEdit && f.suggested_client_charge != null && (
                  <Button size="sm" className="mt-2 h-auto min-h-8 w-full justify-start whitespace-normal py-1.5 text-left bg-[#0E2A47] text-white hover:bg-[#0E2A47]/90 sm:w-auto" onClick={applyApprovedPrice} disabled={applying} data-testid="wo-fin-apply-price">
                    {applying ? <Loader2 size={13} className="mr-1.5 shrink-0 animate-spin" /> : <ArrowRight size={13} className="mr-1.5 shrink-0" />}
                    Use approved price {money(f.suggested_client_charge)} as client charge
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Approved selling price + governing budget */}
          <div className="rounded-lg border border-slate-200 p-3" data-testid="wo-fin-approved">
            <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">Approved Selling Price</div>
            <Amount value={sum.approved_selling_price} missingLabel="Not priced yet" strong testid="wo-fin-approved-amount" />
            {tw ? (
              <div className="mt-1 text-[11px] text-slate-500">Governing budget {tw.reference || '—'}{tw.revision ? ` · rev ${tw.revision}` : ''} · {tw.pricing_status || tw.status}</div>
            ) : (
              <div className="mt-1 text-[11px] italic text-slate-400">No TAG-Work budget linked (direct / contractual work)</div>
            )}
          </div>

          {/* Client billing / invoiced / outstanding */}
          <div className="rounded-lg border border-slate-200 p-3" data-testid="wo-fin-billing">
            <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">Client Billing</div>
            <Row label="Billed to client"><Amount value={cb.client_billed_total} missingLabel="No charge set" strong testid="wo-fin-billed" /></Row>
            <Row label="Invoiced"><Amount value={sum.invoiced_amount} missingLabel="Not invoiced" testid="wo-fin-invoiced" /></Row>
            <Row label="Outstanding"><Amount value={sum.outstanding_amount} missingLabel="—" tone="text-[#8a5a00]" testid="wo-fin-outstanding" /></Row>
          </div>

          {/* Budgeted costs */}
          <div className="rounded-lg border border-slate-200 p-3" data-testid="wo-fin-budgeted">
            <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">Budgeted Cost (estimate)</div>
            {tw ? (<>
              <Row label="Labor"><Amount value={tw.est_labor} /></Row>
              {tw.est_subcontractor ? <Row label="Subcontractor"><Amount value={tw.est_subcontractor} /></Row> : null}
              <Row label="Materials"><Amount value={tw.est_materials} /></Row>
              <Row label="Travel"><Amount value={tw.est_travel} /></Row>
              <Row label="Overhead"><Amount value={tw.est_overhead} /></Row>
              <div className="mt-1 border-t pt-1"><Row label="Estimated total cost"><Amount value={tw.est_total_cost} strong /></Row></div>
            </>) : <div className="py-2 text-xs italic text-slate-400">No budget — nothing to estimate against.</div>}
          </div>

          {/* Actual costs */}
          <div className="rounded-lg border border-slate-200 p-3" data-testid="wo-fin-actual">
            <div className="mb-1 flex items-center justify-between">
              <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Actual Cost (recorded)</div>
              {ac.pending && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500" data-testid="wo-fin-actual-pending">Provisional</span>}
            </div>
            {ac.pending
              ? <div className="py-2 text-xs italic text-slate-400">No actual costs recorded yet. Profitability is provisional until receipts / costs are entered.</div>
              : (<>
                  {ac.components && Object.entries({ Labor: ac.components.labor, Travel: ac.components.travel, Materials: ac.components.materials, Supplies: ac.components.supplies, 'Other direct': ac.components.other_direct })
                    .filter(([, val]) => val)
                    .map(([k, val]) => <Row key={k} label={k}><Amount value={val} /></Row>)}
                  <div className="mt-1 border-t pt-1"><Row label="Actual total cost"><Amount value={ac.actual_total} strong /></Row></div>
                </>)}
          </div>
        </div>

        {/* Budget vs actual + profit */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="wo-fin-variance">
          <div className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500"><TrendingUp size={13} /> Budget vs Actual</div>
          <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-3">
            <Row label="Cost variance" hint="actual − budget"><Amount value={v.cost_variance} missingLabel="—" tone={v.cost_variance > 0 ? 'text-rose-600' : 'text-emerald-700'} testid="wo-fin-cost-variance" /></Row>
            <Row label="Expected profit"><Amount value={v.expected_profit} missingLabel="—" /></Row>
            <Row label={`Actual profit${v.profitability_provisional ? ' (provisional)' : ''}`}><Amount value={v.actual_profit} missingLabel="Pending" tone="text-emerald-700" testid="wo-fin-actual-profit" /></Row>
          </div>
        </div>
        {/* Receipts → verified actual costs */}
        <div className="rounded-lg border border-slate-200 p-3" data-testid="wo-fin-receipts">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500"><Receipt size={13} /> Receipts &amp; Actual Costs</div>
            {canEdit && (
              <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-[#0E2A47] px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-[#0E2A47]/90" data-testid="wo-receipt-upload-label">
                {extracting ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {extracting ? 'Reading…' : 'Capture receipt'}
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onExtract} disabled={extracting} data-testid="wo-receipt-upload-input" />
              </label>
            )}
          </div>

          {draft && (
            <div className="mb-3 rounded-lg border border-[#8FC93A]/50 bg-[#8FC93A]/5 p-3" data-testid="wo-receipt-review">
              {draft.duplicate && <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-amber-700"><AlertTriangle size={13} /> Possible duplicate — saving is blocked unless this is a different receipt.</div>}
              {draft.note && <div className="mb-2 text-xs text-slate-500">{draft.note}</div>}
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                <label className="block text-[11px] text-slate-500">Supplier<input value={draft.supplier} onChange={(e) => setDraft((d) => ({ ...d, supplier: e.target.value }))} data-testid="wo-receipt-supplier" className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-sm text-slate-800" /></label>
                <label className="block text-[11px] text-slate-500">Date<input type="date" value={draft.receipt_date || ''} onChange={(e) => setDraft((d) => ({ ...d, receipt_date: e.target.value }))} data-testid="wo-receipt-date" className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-sm text-slate-800" /></label>
                <label className="block text-[11px] text-slate-500">Reference<input value={draft.receipt_reference} onChange={(e) => setDraft((d) => ({ ...d, receipt_reference: e.target.value }))} data-testid="wo-receipt-ref" className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-sm text-slate-800" /></label>
                <label className="block text-[11px] text-slate-500">Subtotal<input type="number" step="0.01" value={draft.subtotal} onChange={(e) => setDraft((d) => ({ ...d, subtotal: e.target.value }))} data-testid="wo-receipt-subtotal" className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-sm text-slate-800" /></label>
                <label className="block text-[11px] text-slate-500">Tax<input type="number" step="0.01" value={draft.tax} onChange={(e) => setDraft((d) => ({ ...d, tax: e.target.value }))} data-testid="wo-receipt-tax" className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-sm text-slate-800" /></label>
                <label className="block text-[11px] font-semibold text-slate-600">Total *<input type="number" step="0.01" value={draft.total} onChange={(e) => setDraft((d) => ({ ...d, total: e.target.value }))} data-testid="wo-receipt-total" className="mt-0.5 w-full rounded border border-slate-400 px-2 py-1 text-sm font-semibold text-[#0E2A47]" /></label>
              </div>
              <div className="mt-2 flex justify-end gap-2">
                <Button size="sm" variant="outline" className="h-8" onClick={() => setDraft(null)} data-testid="wo-receipt-cancel">Cancel</Button>
                <Button size="sm" className="h-8 bg-[#0E2A47] text-white hover:bg-[#0E2A47]/90" onClick={saveReceipt} disabled={savingR} data-testid="wo-receipt-save">
                  {savingR ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Check size={13} className="mr-1" />} Save as actual cost
                </Button>
              </div>
            </div>
          )}

          {receipts.length === 0 ? (
            <div className="py-1 text-xs italic text-slate-400">No receipts captured yet. Upload a receipt to record actual costs against this work order.</div>
          ) : (
            <div className="space-y-1" data-testid="wo-receipt-list">
              {receipts.map((r) => (
                <div key={r.id} className="flex items-center gap-2 rounded border border-slate-100 bg-slate-50 px-2 py-1.5 text-sm" data-testid={`wo-receipt-row-${r.id}`}>
                  <span className={`inline-flex h-5 items-center rounded-full px-2 text-[10px] font-semibold ${r.verified ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{r.verified ? 'Verified' : 'Pending'}</span>
                  <span className="min-w-0 flex-1 truncate text-slate-700">{r.supplier || 'Receipt'}{r.receipt_reference ? ` · ${r.receipt_reference}` : ''}{r.receipt_date ? ` · ${r.receipt_date}` : ''}</span>
                  <span className="font-semibold text-[#0E2A47]">{money2(r.total)}</span>
                  {r.media_id && <button onClick={() => openReceipt(r)} className="rounded p-1 text-[#2F6DB5] hover:text-[#0E2A47]" title="Open original receipt" data-testid={`wo-receipt-open-${r.id}`}><FileText size={14} /></button>}
                  {canEdit && <button onClick={() => toggleVerify(r)} className="rounded p-1 text-slate-500 hover:text-emerald-700" title={r.verified ? 'Mark pending' : 'Verify'} data-testid={`wo-receipt-verify-${r.id}`}><Check size={14} /></button>}
                  {canEdit && <button onClick={() => removeReceipt(r)} className="rounded p-1 text-rose-500 hover:text-rose-700" title="Remove" data-testid={`wo-receipt-delete-${r.id}`}><Trash2 size={14} /></button>}
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
